// "Open creator portal": an admin temporarily swaps their session for a
// creator's (backend: POST /admin/creators/:id/impersonate). The app keeps a
// single session in localStorage, so the admin's own session is stashed under
// ADMIN_STASH_KEY while impersonating and put back on return.
//
// The impersonation token has no refresh token and lasts an hour; the token
// refresher and the 401 handler call endImpersonationLocally() instead of
// trying to refresh (which would fail and wipe localStorage, stash included).

const ADMIN_STASH_KEY = "impersonation_admin_session";
const INFO_KEY = "impersonation";
const SESSION_KEYS = ["token", "refreshToken", "expireDate", "role", "userInfo", "permissions"];

export const getImpersonation = () => {
  try {
    return JSON.parse(localStorage.getItem(INFO_KEY) || "null");
  } catch {
    return null;
  }
};

export const isImpersonating = () => !!getImpersonation();

// `data` is the impersonate API response (same shape as /login, plus
// `impersonation`). `returnTo` is the admin route to come back to.
export const beginImpersonation = (data, returnTo) => {
  const stash = {};
  SESSION_KEYS.forEach((k) => {
    stash[k] = localStorage.getItem(k);
  });
  localStorage.setItem(ADMIN_STASH_KEY, JSON.stringify(stash));
  localStorage.setItem(INFO_KEY, JSON.stringify({ ...data.impersonation, return_to: returnTo }));

  localStorage.setItem("token", data.token);
  localStorage.removeItem("refreshToken");
  localStorage.setItem("expireDate", String(data.expire_date));
  localStorage.setItem("role", "Creator");
  localStorage.setItem("userInfo", JSON.stringify(data));
  localStorage.setItem("permissions", JSON.stringify(data.user?.permissions || {}));

  // Full reload so every cached view (sidebar, RBAC, profile) starts over as
  // the creator.
  window.location.hash = "#/mainlayout/creator-movie";
  window.location.reload();
};

// After POST /impersonation/extend: swap in the new token/expiry without a
// reload. Returns the updated info.
export const applyImpersonationExtension = ({ token, expire_date, expires_at }) => {
  const info = { ...getImpersonation(), expires_at };
  localStorage.setItem("token", token);
  localStorage.setItem("expireDate", String(expire_date));
  localStorage.setItem(INFO_KEY, JSON.stringify(info));
  return info;
};

let ending = false;

// Put the admin's session back and reload on the admin page they came from.
// Purely local — the caller ends the session server-side first when it can.
export const endImpersonationLocally = () => {
  if (ending) return;
  ending = true;
  const info = getImpersonation();
  // Another tab already put the admin session back (its stash is gone):
  // restoring "nothing" here would wipe that session. Just pick it up.
  if (!info) {
    window.location.reload();
    return;
  }
  let stash = null;
  try {
    stash = JSON.parse(localStorage.getItem(ADMIN_STASH_KEY) || "null");
  } catch {
    stash = null;
  }

  SESSION_KEYS.forEach((k) => {
    const v = stash ? stash[k] : null;
    if (v === null || v === undefined) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  });
  localStorage.removeItem(ADMIN_STASH_KEY);
  localStorage.removeItem(INFO_KEY);

  // No stashed admin session to go back to: land on the login page.
  window.location.hash = stash?.token ? `#${info?.return_to || "/mainlayout/monetization/creators"}` : "#/";
  window.location.reload();
};
